<template>
  <section :class="wrapperClasses" class="mds-card">
    <header v-if="haveHeaderSlot" v-on="headerListeners" :class="headerClasses">
      <slot name="header"/>

      <div class="buttons-wrap" v-if="haveButtons">
        <template v-if="isCollapsible">
          <button :class="collapseButtonClasses" v-if="isCollapseTriangle">
            <template v-if="collapsed">
              <VIcon material="arrow_drop_down"/>
            </template>
            <template v-else>
              <VIcon material="arrow_drop_up"/>
            </template>
          </button>

          <button
              :class="collapseButtonClasses"
              v-if="isCollapseSwitch"
          ></button>
        </template>

        <button @click.stop="onCloseButtonClick" class="btn-close" v-if="closable">
          <VIcon material="close"/>
        </button>
      </div>
    </header>

    <div
        :class="{ 'with-footer': hasFooterSlot, 'with-header': haveHeaderSlot }"
        class="content"
    >
      <slot/>
    </div>

    <footer v-if="hasFooterSlot">
      <slot name="footer"/>
    </footer>
  </section>
</template>

<script lang="ts">
import VIcon from "@/lib/components/VIcon/VIcon.vue";

const COLLAPSE_THEMES = ["triangle", "switcher"];

export default defineComponent({
  name: "VCard",
  components: {VIcon},
  props: {
    noPadding: {
      type: Boolean,
      default: false,
    },
    collapsed: {
      type: Boolean | null,
      default: null,
    },
    collapseTheme: {
      type: String,
      default: "triangle",
      validator: (value) => COLLAPSE_THEMES.includes(value),
    },
    closable: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["closeButtonClick", "collapsed:update"],

  data() {
    return {};
  },

  computed: {
    haveHeaderSlot() {
      return this.$slots["header"] || this.haveButtons;
    },

    hasFooterSlot() {
      return this.$slots["footer"];
    },

    isCollapsible() {
      return typeof this.collapsed === "boolean";
    },

    headerListeners() {
      return {click: (this.isCollapsible) ? this.onHeaderClick : null};
    },

    wrapperClasses() {
      return {
        "no-padding": this.noPadding,
        collapsed: this.collapsed,
        collapsible: this.isCollapsible,
      };
    },

    isCollapseTriangle() {
      return this.collapseTheme === "triangle";
    },

    isCollapseSwitch() {
      return this.collapseTheme === "switch";
    },

    collapseButtonClasses() {
      return {
        "btn-collapse": true,
        switch: this.isCollapseSwitch,
        triangle: this.isCollapseTriangle,
        collapsed: this.collapsed,
      };
    },

    haveButtons() {
      return this.isCollapsible || this.closable;
    },

    headerClasses() {
      return {
        closable: this.closable,
        collapsable: this.isCollapsible,
      };
    },
  },

  methods: {
    onCloseButtonClick() {
      this.$emit("closeButtonClick");
    },

    onHeaderClick(event) {
      if (!(this.closable && (event.target.classList.contains("btn-close") || event.target.parentElement.classList.contains("btn-close")))) {
        this.collapsed = !this.collapsed;
        this.$emit("collapsed:update", this.collapsed);
      }
    },
  }
});
</script>

<style lang="scss" src="./VCard.scss"/>
